import React, { ReactElement } from 'react';
import { Autocomplete, Box, Grid, Typography } from '@mui/material';
import { EnrichedAutocompleteResult } from '../types';
import { generateDataTestId } from '../../../../utils';
import { LABELS } from '../constants';

interface AddressSearchFieldProps {
  inputValue: string;
  addressOptions: EnrichedAutocompleteResult[];
  selectedPrediction: EnrichedAutocompleteResult | null;
  isLoading: boolean;
  isTaxSearchPending: boolean;
  locationType: string;
  noTaxRecordModeRef: React.MutableRefObject<boolean>;
  handleAddressChange: (
    event: React.SyntheticEvent,
    value: EnrichedAutocompleteResult | null,
  ) => void;
  setInputValue: (value: string) => void;
  renderTextFieldInput: (params?: any, value?: string) => ReactElement;
  formatAddress: (result: EnrichedAutocompleteResult) => string;
  compareEnrichedResults: (
    option: EnrichedAutocompleteResult,
    value: EnrichedAutocompleteResult,
  ) => boolean;
  isRealistFlow?: boolean;
}

/**
 * AddressSearchField component - handles the address/tax ID input
 *
 * This component provides an autocomplete field for searching addresses or tax IDs
 * with proper handling of loading states and result formatting.
 */
const AddressSearchField: React.FC<AddressSearchFieldProps> = ({
  inputValue,
  addressOptions,
  selectedPrediction,
  isLoading,
  isTaxSearchPending,
  locationType,
  noTaxRecordModeRef,
  handleAddressChange,
  setInputValue,
  renderTextFieldInput,
  formatAddress,
  compareEnrichedResults,
  isRealistFlow = false,
}) => {
  const label = isRealistFlow
    ? LABELS.realistAddressSearch
    : LABELS.standardAddressSearch;

  return (
    <Box
      pr={{
        xl: 20,
      }}
    >
      <Typography
        fontWeight="bold"
        mt={{
          md: 3,
        }}
        sx={{ color: locationType !== 'address' ? 'text.disabled' : null }}
        mb={-1}
      >
        {label}
      </Typography>
      {noTaxRecordModeRef.current || locationType !== 'address' ? (
        renderTextFieldInput(
          {
            disabled: true,
          },
          inputValue,
        )
      ) : (
        <Autocomplete
          data-testid={generateDataTestId(label, undefined, 'textField')}
          fullWidth
          options={addressOptions}
          inputValue={inputValue}
          getOptionLabel={(option) => formatAddress(option)}
          isOptionEqualToValue={compareEnrichedResults}
          renderInput={renderTextFieldInput}
          value={selectedPrediction}
          onChange={handleAddressChange}
          onInputChange={(event, newInputValue, reason) => {
            // Block reset events that try to clear a valid address
            // This prevents the Autocomplete from clearing when re-enabled after mode switch
            if (reason === 'reset' && newInputValue === '' && inputValue) {
              return;
            }

            setInputValue(newInputValue);
          }}
          renderOption={(props, option) => {
            const { key, ...otherProps } = props;
            return (
              <li key={key} {...otherProps}>
                <Grid container>
                  <Grid size={12}>
                    <Typography variant="body1">
                      {formatAddress(option)}
                    </Typography>
                  </Grid>
                  {option.enrichedData && (
                    <Grid container>
                      <Typography
                        variant="caption"
                        sx={{ color: 'text.secondary' }}
                      >
                        {option.enrichedData.TaxRecord &&
                        option.enrichedData.TaxRecord.TaxId
                          ? `Tax ID: ${option.enrichedData.TaxRecord.TaxId} | `
                          : ''}
                        {option.enrichedData.Listings.length} Listing
                        {option.enrichedData.Listings.length !== 1 ? 's' : ''}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </li>
            );
          }}
          noOptionsText={
            isLoading ? (
              'Loading...'
            ) : isTaxSearchPending && addressOptions.length === 0 ? (
              <Typography>Searching Tax Records...</Typography>
            ) : (
              'No address found'
            )
          }
          filterOptions={(x) => x} // Disable built-in filtering
        />
      )}
    </Box>
  );
};

export default AddressSearchField;
